import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Icon } from '@/kit/Icon';
import { Tooltip } from '@/kit/Tooltip';
import './SectionTabs.scss';

export interface SectionTab {
  id: string;
  label: ReactNode;
  /** A count shown beside the label (e.g. the notes in a tab). */
  badge?: number | string;
  disabled?: boolean;
  /** Shown as an info icon + tooltip next to the label, only while this tab is active. */
  infoText?: string;
}

interface SectionTabsProps {
  tabs: SectionTab[];
  /** Controlled selection; leave undefined to let the strip keep it (starting at `defaultValue` or the first tab). */
  value?: string;
  defaultValue?: string;
  onChange?: (id: string) => void;
  /** Names the tab list for assistive technology. */
  ariaLabel: string;
  /** The panel of the selected tab. Only that panel is rendered. */
  children: (activeId: string) => ReactNode;
}

/**
 * Design review 2 Nov 2026: long settings pages become tabs (Additional Notes, Create New Report, Capium Tags), "any number of
 * tabs". The look of the Compliance & Filing / Adjustments & Reports bars; a row that scrolls sideways when the tabs do not fit.
 * (`TabGroup` is the Material look and has no sideways scroll or arrow keys, so it is not used here.)
 */
export function SectionTabs({ tabs, value, defaultValue, onChange, ariaLabel, children }: SectionTabsProps) {
  const baseId = useId();
  const [internal, setInternal] = useState(defaultValue ?? tabs[0]?.id ?? '');
  const active = value ?? internal;
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const enabled = tabs.filter((tab) => !tab.disabled);
  const list = useRef<HTMLDivElement>(null);
  /** Whether tabs are hidden beyond the left / right edge of the row: shows the scroll arrows (3 Nov design review). */
  const [more, setMore] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const node = list.current;
    if (!node) return;
    const left = node.scrollLeft > 1;
    const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
    setMore((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    measure();
    const node = list.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure, tabs.length]);

  const scrollBy = (direction: -1 | 1) => {
    const node = list.current;
    if (!node) return;
    const amount = Math.max(node.clientWidth * 0.6, 120) * direction;
    if (typeof node.scrollBy === 'function') node.scrollBy({ left: amount, behavior: 'smooth' });
    else node.scrollLeft += amount;
  };

  const select = (id: string) => {
    if (value === undefined) setInternal(id);
    if (id !== active) onChange?.(id);
  };

  // Arrow keys, Home and End move to the next enabled tab and open it (automatic activation).
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, id: string) => {
    const at = enabled.findIndex((tab) => tab.id === id);
    const target =
      event.key === 'ArrowRight'
        ? enabled[(at + 1) % enabled.length]
        : event.key === 'ArrowLeft'
          ? enabled[(at - 1 + enabled.length) % enabled.length]
          : event.key === 'Home'
            ? enabled[0]
            : event.key === 'End'
              ? enabled[enabled.length - 1]
              : undefined;
    if (!target) return;
    event.preventDefault();
    refs.current.get(target.id)?.focus();
    select(target.id);
  };

  const tabId = (id: string) => `${baseId}-tab-${id}`;
  const panelId = (id: string) => `${baseId}-panel-${id}`;

  return (
    <div className="kit-section-tabs">
      <div className={`kit-section-tabs__bar${more.left ? ' more-left' : ''}${more.right ? ' more-right' : ''}`}>
        {more.left && (
          <button
            type="button"
            tabIndex={-1}
            className="kit-section-tabs__arrow left"
            aria-label="Show earlier tabs"
            onClick={() => scrollBy(-1)}
          >
            ‹
          </button>
        )}
        <div className="kit-section-tabs__list" role="tablist" aria-label={ariaLabel} ref={list} onScroll={measure}>
          {tabs.map((tab) => {
            const selected = tab.id === active;
            return (
              <button
                key={tab.id}
                ref={(node) => {
                  if (node) refs.current.set(tab.id, node);
                  else refs.current.delete(tab.id);
                }}
                id={tabId(tab.id)}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={panelId(tab.id)}
                tabIndex={selected ? 0 : -1}
                disabled={tab.disabled}
                className={`kit-section-tabs__tab${selected ? ' active' : ''}`}
                onClick={() => select(tab.id)}
                onKeyDown={(event) => onKeyDown(event, tab.id)}
              >
                <span>{tab.label}</span>
                {/* a plain space between the label and the count, so it reads "Notes 3" and not "Notes3" */}
                {tab.badge !== undefined && ' '}
                {tab.badge !== undefined && <span className="kit-section-tabs__badge">{tab.badge}</span>}
                {selected && tab.infoText && (
                  <Tooltip title={tab.infoText}>
                    <Icon name="info" outlined className="kit-section-tabs__info" />
                  </Tooltip>
                )}
              </button>
            );
          })}
        </div>
        {more.right && (
          <button
            type="button"
            tabIndex={-1}
            className="kit-section-tabs__arrow right"
            aria-label="Show more tabs"
            onClick={() => scrollBy(1)}
          >
            ›
          </button>
        )}
      </div>
      <div id={panelId(active)} role="tabpanel" aria-labelledby={tabId(active)} className="kit-section-tabs__panel">
        {children(active)}
      </div>
    </div>
  );
}
